import { createContext, useContext, type ReactNode } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import type { Platform } from '../types';

export interface Settings {
  defaultPlatforms: Platform[];
  linkExpiryDays: number; // 0 = never expires
  brandName: string;
}

// File upload limits (in bytes)
export const UPLOAD_LIMITS = {
  IMAGE_MAX_SIZE: 5 * 1024 * 1024, // 5MB
  VIDEO_MAX_SIZE: 25 * 1024 * 1024, // 25MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/quicktime', 'video/webm'],
};

const DEFAULT_SETTINGS: Settings = {
  defaultPlatforms: ['linkedin'],
  linkExpiryDays: 7,
  brandName: '',
};

interface SettingsContextValue {
  settings: Settings;
  updateSettings: (updates: Partial<Settings>) => void;
  resetSettings: () => void;
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useLocalStorage<Settings>('doopl_settings', DEFAULT_SETTINGS);

  const updateSettings = (updates: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

// Default value for when context isn't available yet (during initial render)
const DEFAULT_SETTINGS_VALUE: SettingsContextValue = {
  settings: DEFAULT_SETTINGS,
  updateSettings: () => {},
  resetSettings: () => {},
};

export function useSettings() {
  const context = useContext(SettingsContext);
  // Return default value if context not ready (prevents crashes during initial render)
  return context || DEFAULT_SETTINGS_VALUE;
}
